import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import ViewLayout from '../../../components/Common/ViewLayout';

interface DriverFormData {
  nome: string;
  cpf: string;
  rg?: string;
  data_nascimento?: string;
  telefone?: string;
  celular: string;
  lid?: string;
  email?: string;
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  cnh_numero: string;
  cnh_categoria?: string;
  cnh_vencimento: string;
  cnh_primeira_habilitacao?: string;
  cnh_pontos_atuais?: number;
  foto_motorista?: string;
  foto_cnh_frente?: string;
  foto_cnh_verso?: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  data_admissao?: string;
  data_demissao?: string;
  salario?: number;
  comissao_percentual?: number;
  banco?: string;
  agencia?: string;
  conta?: string;
  pix_chave?: string;
  observacoes?: string;
  status: 'ativo' | 'inativo';
  avaliacao_comportamental?: string;
  numero_viagens_realizadas?: number;
  quilometragem_total?: number;
}

const DriverCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<DriverFormData>({
    nome: '',
    cpf: '',
    celular: '',
    cnh_numero: '',
    cnh_vencimento: '',
    status: 'ativo',
    cnh_pontos_atuais: 0,
    numero_viagens_realizadas: 0,
    quilometragem_total: 0
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    if (errors[name as keyof DriverFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatCEP = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 8) {
      return cleaned.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({ ...prev, cpf: formatted }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, [e.target.name]: formatted }));
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, endereco_cep: formatted }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome completo é obrigatório';
    }

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    }

    if (!formData.celular.trim()) {
      newErrors.celular = 'Celular é obrigatório';
    }

    if (!formData.cnh_numero.trim()) {
      newErrors.cnh_numero = 'Número da CNH é obrigatório';
    }

    if (!formData.cnh_vencimento) {
      newErrors.cnh_vencimento = 'Validade da CNH é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.createDriver(formData);
      
      if (response.success) {
        utils.showNotification('Motorista criado com sucesso!', { type: 'success' });
        navigate('/motoristas');
      } else {
        throw new Error(response.message || 'Erro ao criar motorista');
      }
    } catch (error: any) {
      console.error('Erro ao criar motorista:', error);
      utils.showNotification(error.message || 'Erro ao criar motorista', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/motoristas');
  };

  return (
    <ViewLayout
      title="Novo Motorista"
      subtitle="Criação de Motorista"
      isLoading={isLoading}
      onBack={handleCancel}
    >
      <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Informações Básicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className={`form-control ${errors.nome ? 'error' : ''}`}
                      placeholder="Digite o nome completo"
                    />
                    {errors.nome && <span className="error-message">{errors.nome}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">CPF *</label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleCpfChange}
                      className={`form-control ${errors.cpf ? 'error' : ''}`}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    {errors.cpf && <span className="error-message">{errors.cpf}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Celular *</label>
                    <input
                      type="text"
                      name="celular"
                      value={formData.celular}
                      onChange={handlePhoneChange}
                      className={`form-control ${errors.celular ? 'error' : ''}`}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                    {errors.celular && <span className="error-message">{errors.celular}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">LID (WhatsApp)</label>
                    <input
                      type="text"
                      name="lid"
                      value={formData.lid || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="ID vinculado do WhatsApp"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número da CNH *</label>
                    <input
                      type="text"
                      name="cnh_numero"
                      value={formData.cnh_numero}
                      onChange={handleInputChange}
                      className={`form-control ${errors.cnh_numero ? 'error' : ''}`}
                      placeholder="Número da CNH"
                    />
                    {errors.cnh_numero && <span className="error-message">{errors.cnh_numero}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Validade CNH *</label>
                    <input
                      type="date"
                      name="cnh_vencimento"
                      value={formData.cnh_vencimento}
                      onChange={handleInputChange}
                      className={`form-control ${errors.cnh_vencimento ? 'error' : ''}`}
                    />
                    {errors.cnh_vencimento && <span className="error-message">{errors.cnh_vencimento}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">RG</label>
                    <input
                      type="text"
                      name="rg"
                      value={formData.rg || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número do RG"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Nascimento</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      name="telefone"
                      value={formData.telefone || ''}
                      onChange={handlePhoneChange}
                      className="form-control"
                      placeholder="(00) 0000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="email@exemplo.com"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Endereço
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Rua</label>
                    <input
                      type="text"
                      name="endereco_rua"
                      value={formData.endereco_rua || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome da rua"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número</label>
                    <input
                      type="text"
                      name="endereco_numero"
                      value={formData.endereco_numero || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nº"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Complemento</label>
                    <input
                      type="text"
                      name="endereco_complemento"
                      value={formData.endereco_complemento || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Apto, bloco, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bairro</label>
                    <input
                      type="text"
                      name="endereco_bairro"
                      value={formData.endereco_bairro || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do bairro"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cidade</label>
                    <input
                      type="text"
                      name="endereco_cidade"
                      value={formData.endereco_cidade || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome da cidade"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <input
                      type="text"
                      name="endereco_estado"
                      value={formData.endereco_estado || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">CEP</label>
                    <input
                      type="text"
                      name="endereco_cep"
                      value={formData.endereco_cep || ''}
                      onChange={handleCepChange}
                      className="form-control"
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>
                </div>
              </div>

              {/* Documentação - CNH */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Documentação - CNH
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Categoria CNH</label>
                    <input
                      type="text"
                      name="cnh_categoria"
                      value={formData.cnh_categoria || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: AB, C, D, E"
                      maxLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Primeira Habilitação</label>
                    <input
                      type="date"
                      name="cnh_primeira_habilitacao"
                      value={formData.cnh_primeira_habilitacao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Pontos na CNH</label>
                    <input
                      type="number"
                      name="cnh_pontos_atuais"
                      value={formData.cnh_pontos_atuais || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      max="20"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Contato de Emergência */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Contato de Emergência
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <input
                      type="text"
                      name="contato_emergencia_nome"
                      value={formData.contato_emergencia_nome || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do contato de emergência"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      name="contato_emergencia_telefone"
                      value={formData.contato_emergencia_telefone || ''}
                      onChange={handlePhoneChange}
                      className="form-control"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Parentesco</label>
                    <input
                      type="text"
                      name="contato_emergencia_parentesco"
                      value={formData.contato_emergencia_parentesco || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Parentesco com o motorista"
                    />
                  </div>
                </div>
              </div>

              {/* Dados Profissionais */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Dados Profissionais
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Data de Admissão</label>
                    <input
                      type="date"
                      name="data_admissao"
                      value={formData.data_admissao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Demissão</label>
                    <input
                      type="date"
                      name="data_demissao"
                      value={formData.data_demissao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Salário (R$)</label>
                    <input
                      type="number"
                      name="salario"
                      value={formData.salario || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Comissão (%)</label>
                    <input
                      type="number"
                      name="comissao_percentual"
                      value={formData.comissao_percentual || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="0,00"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Banco</label>
                    <input
                      type="text"
                      name="banco"
                      value={formData.banco || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do banco"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Agência</label>
                    <input
                      type="text"
                      name="agencia"
                      value={formData.agencia || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número da agência"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conta</label>
                    <input
                      type="text"
                      name="conta"
                      value={formData.conta || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número da conta"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Chave PIX</label>
                    <input
                      type="text"
                      name="pix_chave"
                      value={formData.pix_chave || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Chave PIX"
                    />
                  </div>
                </div>
              </div>

              {/* Avaliação e Observações */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Avaliação e Observações
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Avaliação Comportamental</label>
                    <input
                      type="text"
                      name="avaliacao_comportamental"
                      value={formData.avaliacao_comportamental || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Avaliação comportamental"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Observações</label>
                    <textarea
                      name="observacoes"
                      value={formData.observacoes || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      rows={4}
                      placeholder="Observações importantes sobre o motorista"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Salvar Motorista
                    </>
                  )}
                </button>
              </div>
      </form>
    </ViewLayout>
  );
};

export default DriverCreate;
